import { useState } from "react";
import { Link } from "react-router-dom";
import type {
  HandoffDto,
  SupportEscalationThread,
} from "@care-by-ella/contracts";
import { fetchSupportEscalationThread } from "@/lib/handoff-api";

// TES-1110 — a handoff escalated from the patient's Ella Support thread
// links to the message instead of copying it. This is that link, for the
// doctor or coach working the handoff: it reads the thread on demand through
// the escalation (a patient-scoped, logged read), with the escalated message
// marked.
//
// The admin's read-only view of a clinician queue cannot use that route
// (doctor and coach only); she reads the same thread in her own Support
// inbox, so she gets a link there instead.

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleString("sv-SE", {
    timeZone: "Europe/Stockholm",
    dateStyle: "short",
    timeStyle: "short",
  });
}

export function SupportEscalationMessages({
  handoff,
  readOnly,
}: {
  handoff: Pick<HandoffDto, "id" | "patientId" | "source" | "sourceMessageId">;
  readOnly: boolean;
}) {
  const [thread, setThread] = useState<SupportEscalationThread | null>(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (handoff.source !== "support_thread") return null;
  const label = handoff.sourceMessageId
    ? "meddelandet i Ella Support"
    : "tråden i Ella Support";

  if (readOnly) {
    return (
      <Link
        to={`/support-inbox/${encodeURIComponent(handoff.patientId)}`}
        className="text-sm text-brand underline"
      >
        Läs {label}
      </Link>
    );
  }

  async function toggle() {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (thread) return;
    setLoading(true);
    setError(null);
    try {
      setThread(
        await fetchSupportEscalationThread(handoff.patientId, handoff.id),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-xs">
      <button
        type="button"
        onClick={() => void toggle()}
        aria-expanded={open}
        className="self-start text-sm text-brand underline"
      >
        {open ? `Dölj ${label}` : `Läs ${label}`}
      </button>
      {open ? (
        <div className="rounded-sm border border-surfaceMuted bg-background p-sm">
          {loading ? (
            <p className="text-xs text-textSecondary">Hämtar…</p>
          ) : null}
          {error ? <p className="text-xs text-danger">{error}</p> : null}
          {thread && thread.messages.length === 0 ? (
            <p className="text-xs text-textSecondary">Inga meddelanden.</p>
          ) : null}
          <ol className="flex flex-col gap-xs" aria-label="Ella Support">
            {thread?.messages.map((m) => {
              const linked = m.id === thread.linkedMessageId;
              return (
                <li
                  key={m.id}
                  aria-current={linked ? "true" : undefined}
                  className={`rounded-sm p-xs text-sm ${
                    linked
                      ? "border border-warning/60 bg-warning/10"
                      : m.senderType === "patient"
                        ? "bg-surface"
                        : "bg-brand/5"
                  }`}
                >
                  <p className="text-xs text-textSecondary">
                    {m.senderType === "patient" ? "Patienten" : "Ella Support"}{" "}
                    · {fmtTime(m.createdAt)}
                    {linked ? " · Eskalerat meddelande" : ""}
                  </p>
                  <p className="whitespace-pre-wrap">{m.body}</p>
                </li>
              );
            })}
          </ol>
        </div>
      ) : null}
    </div>
  );
}
